import { I80Element, define, h, lateLabel } from "../base.js";
import { icon } from "../icons.js";

const COLORS = ["primary", "secondary", "tertiary", "critical"];
const SIZES = ["small", "medium", "large"];

/**
 * <i80-button color="primary" size="medium" icon="plus" icon-position="leading">Save</i80-button>
 *
 * Renders a real <button> (or <a> with href). A <button> or <a> written inside the tag
 * is used as is, so type, name, value, form and onclick keep working:
 *   <i80-button color="primary"><button type="submit" name="save">Save</button></i80-button>
 */
export class I80Button extends I80Element {
  static get observedAttributes() {
    return ["color", "size", "icon", "icon-position", "icon-only", "full-width", "inline", "href", "disabled", "text", "external"];
  }

  setup() {
    let control = this.part("control") ?? this.child("button, a");
    const href = this.getAttribute("href");
    if (!control) {
      control = href ? h("a") : h("button", { type: this.getAttribute("type") || "button" });
      control.append(...this.takeContent());
      this.append(control);
    }
    control.setAttribute("data-i80-part", "control");
    // The label: author text inside the control (or the text attribute), without our icon/text spans.
    const existing = control.querySelector(":scope > .i80-button__text");
    this._label = this.getAttribute("text") ?? (existing ? existing.textContent : control.textContent).trim();
    this._control = control;
  }

  adopt() {
    const label = lateLabel(this);
    if (label) this._label = label;
  }

  update() {
    const c = this._control;
    const size = this.choice("size", SIZES, "medium");
    const iconOnly = this.flag("icon-only");
    const name = this.getAttribute("icon");
    const label = this.getAttribute("text") ?? this._label;

    c.className = [
      "i80-button",
      `i80-button--color-${this.choice("color", COLORS, "primary")}`,
      this.flag("full-width") && "i80-button--width-full",
      iconOnly && "i80-button--is-icon-only",
      this.flag("inline") && "i80-button--is-inline",
      `i80-button--size-${size}`,
    ].filter(Boolean).join(" ");

    if (c.localName === "a") {
      const href = this.getAttribute("href");
      if (href !== null) c.setAttribute("href", href);
      if (this.flag("external")) {
        c.setAttribute("target", "_blank");
        c.setAttribute("rel", "noopener noreferrer");
      }
    } else {
      c.disabled = this.flag("disabled");
    }

    const iconEl = name ? h("span", { class: "i80-button__icon" }, icon(name, { size: size === "large" ? "24" : "16", stretched: true })) : null;
    const text = h("span", { class: "i80-button__text" }, label);
    if (iconOnly) {
      c.replaceChildren(...(iconEl ? [iconEl] : [text]));
      c.setAttribute("aria-label", label);
    } else {
      c.removeAttribute("aria-label");
      const trailing = this.choice("icon-position", ["leading", "trailing"], "leading") === "trailing";
      c.replaceChildren(...(iconEl ? (trailing ? [text, iconEl] : [iconEl, text]) : [text]));
    }
  }
}

define("i80-button", I80Button);
